import { useCallback } from 'react'
import type { FormResponse } from '../utils/form'

/**
 * from を指定したときは送信元のパスではなく、その値で固定する。
 * GTM の CV トリガーは from の値（例: `/thanks_download/?from=request` を含む）で判定しているため、
 * 送信元のパスが変わっても既存の CV が発火するようにしたいフォームで使う
 */
const createThanksPath = (to: string, from?: string) => {
  const path = location.pathname.split('/')
  if (path[0] === '') {
    path.splice(0, 1)
  }
  if (path[path.length - 1] === '') {
    path.splice(path.length - 1, 1)
  }
  if (from) return `${to}?from=${from}`
  const p = `${to}?from=${path.join('-')}`
  return p
}

/** 送信エラーページ（全フォーム共通）へ遷移する。サンクスページと同じく from に送信元を付ける */
export const goToMailError = () => {
  location.href = createThanksPath('/mail_error/')
}

// isSuccessCondition関数の型定義を追加
type SuccessConditionFunc = (json: any, res: Response) => boolean

export const useSendApi = (
  apiPath: string,
  thanksPagePath: string,
  isSuccessCondition?: SuccessConditionFunc,
  thanksFrom?: string
) => {
  /** 送信に成功したら true、失敗したら送信エラーページへ遷移して false を返す */
  const sendApi = useCallback(
    async (body: string): Promise<boolean> => {
      try {
        const res = await fetch(
          `${process.env.NEXT_PUBLIC_FORM_ENDPOINT}${apiPath}`,
          {
            method: 'POST',
            mode: 'cors',
            headers: { 'Content-Type': 'application/json' },
            body
          }
        )
        // 500 系はJSON以外のボディが返ることがあるため、パース失敗はnull扱いにする
        const json = (await res.json().catch(() => null)) as FormResponse | null

        // Default success condition
        let isSuccess = res.status === 200 && json?.success === true

        // Custom success condition if provided
        if (isSuccessCondition) {
          isSuccess = isSuccessCondition(json, res)
        }

        if (!isSuccess) {
          console.error('API Error:', json)
          throw new Error(
            'API response indicates failure: ' + JSON.stringify(json)
          )
        }

        location.href = createThanksPath(thanksPagePath, thanksFrom)
        return true
      } catch {
        goToMailError()
        return false
      }
    },
    [apiPath, isSuccessCondition, thanksPagePath, thanksFrom]
  )

  return {
    sendApi
  }
}
